import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Content } from '@mira-avant/mtlibs';
import { ContentCreatePayload, ContentUpdatePayload } from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class ContentService {
	private readonly BASE_PATH = 'contents';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public contents$: BehaviorSubject<Content[]> = new BehaviorSubject<Content[]>([]);

	constructor(
		private api: ApiService,
		private languageService: LanguageService
	) {}

	public async onActivityChange$(activityUuid: string | undefined): Promise<void> {
		this.contents$.next([]);

		if (!activityUuid) {
			return;
		}

		await this.getByActivity$(activityUuid);
	}

	public setActiveText(content: Content): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;
		const activeText = content.content_text.find(contentText => contentText.default_lang_id === currentLang);
		content.active_text = activeText || content.content_text[0];
	}

	public sortContents(contents: Content[]): Content[] {
		return contents.sort((a: Content, b: Content) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}

	public processContent(content: Content): Content {
		this.setActiveText(content);
		return content;
	}

	public processContents(contents: Content[]): Content[] {
		return this.sortContents(contents.map(content => this.processContent(content)));
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Content> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const content = await firstValueFrom(
				this.api.get<Content>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
			return this.processContent(content);
		});
	}

	public async getRubricCode$(uuid: string): Promise<string> {
		return this.withLoading(async () => {
			const rubricCode = await firstValueFrom(
				this.api.get<string>(`${this.BASE_PATH}/get-rubric-code-by-uuid/${uuid}`)
			);

			return rubricCode;
		});
	}

	public async create$(contentData: ContentCreatePayload): Promise<Content> {
		return this.withLoading(async () => {
			const newContent = await firstValueFrom(this.api.post<Content>(this.BASE_PATH, contentData));

			const processedContent = this.processContent(newContent);
			const currentContents = this.contents$.value;
			const updatedContents = this.processContents([...currentContents, processedContent]);
			this.contents$.next(updatedContents);
			return processedContent;
		});
	}

	public async update$(uuid: string, contentData: ContentUpdatePayload): Promise<Content> {
		return this.withLoading(async () => {
			const updatedContent = await firstValueFrom(
				this.api.put<Content>(`${this.BASE_PATH}/${uuid}`, contentData)
			);

			const processedContent = this.processContent(updatedContent);
			const currentContents = this.contents$.value;
			const updatedContents = this.processContents(
				currentContents.map(content => (content.uuid === uuid ? processedContent : content))
			);
			this.contents$.next(updatedContents);
			return processedContent;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));

			const currentContents = this.contents$.value;
			const updatedContents = currentContents.filter(content => content.uuid !== uuid);
			this.contents$.next(updatedContents);
		});
	}

	public async getByActivity$(
		activityUuid: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Content[]> {
		return this.withLoading(async () => {
			const params = {
				activityUuid,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const contents = await firstValueFrom(this.api.get<Content[]>(`${this.BASE_PATH}/activity`, { params }));

			const processedContents = this.processContents(contents);
			this.contents$.next(processedContents);
			return processedContents;
		});
	}

	public async getByContentType$(
		contentType: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Content[]> {
		return this.withLoading(async () => {
			const params = {
				contentType,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const contents = await firstValueFrom(this.api.get<Content[]>(`${this.BASE_PATH}/type`, { params }));

			const processedContents = this.processContents(contents);
			this.contents$.next(processedContents);
			return processedContents;
		});
	}

	public async duplicate$(uuid: string, activityParentUuid: string): Promise<Content> {
		return this.withLoading(async () => {
			const duplicatedContent = await firstValueFrom(
				this.api.post<Content>(`${this.BASE_PATH}/duplicate/${uuid}/${activityParentUuid}`, {})
			);

			const processedContent = this.processContent(duplicatedContent);
			const currentContents = this.contents$.value;
			const updatedContents = this.processContents([...currentContents, processedContent]);
			this.contents$.next(updatedContents);
			return processedContent;
		});
	}

	public async duplicateBulk$(uuids: string[], activityParentUuid: string): Promise<Content[]> {
		return this.withLoading(async () => {
			const duplicatedContents = await firstValueFrom(
				this.api.post<Content[]>(`${this.BASE_PATH}/duplicate-bulk/${activityParentUuid}`, { uuids })
			);
			const processedContents = this.processContents(duplicatedContents);
			const currentContents = this.contents$.value;
			const newUuidSet = new Set(processedContents.map(content => content.uuid));
			const filteredCurrent = currentContents.filter(content => !newUuidSet.has(content.uuid));
			const updatedContents = this.processContents([...processedContents, ...filteredCurrent]);
			this.contents$.next(updatedContents);
			return processedContents;
		});
	}

	public async moveUp$(uuid: string, activityUuid: string): Promise<Content[]> {
		return this.withLoading(async () => {
			const updatedContents = await firstValueFrom(
				this.api.post<Content[]>(`${this.BASE_PATH}/move-up/${uuid}/${activityUuid}`, {})
			);

			const processedContents = this.processContents(updatedContents);
			this.contents$.next(processedContents);
			return processedContents;
		});
	}

	public async moveDown$(uuid: string, activityUuid: string): Promise<Content[]> {
		return this.withLoading(async () => {
			const updatedContents = await firstValueFrom(
				this.api.post<Content[]>(`${this.BASE_PATH}/move-down/${uuid}/${activityUuid}`, {})
			);

			const processedContents = this.processContents(updatedContents);
			this.contents$.next(processedContents);
			return processedContents;
		});
	}
}
